import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertOctagon,
  Bot,
  DatabaseBackup,
  HardDrive,
  Mail,
  Mic,
  Radio,
  RefreshCw,
  Server,
  Timer,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { relativeTime, shortTime } from '@/lib/format';
import { humanizeSystemError, NIVEL_META, type NivelError } from '@/lib/system-error-humanize';
import { Cargando } from '@/components/common/Cargando';
import { Saneamiento } from '@/components/sistema/Saneamiento';
import {
  getSystemStatus,
  type AgenteStatus,
  type CanalStatus,
  type WorkerStatus,
} from '@/api/system.api';

/**
 * Sala de Máquinas: todo lo vivo del sistema, en español llano y con
 * semáforos. Auto-refresh cada 10 s.
 */

type Semaforo = 'verde' | 'gris' | 'rojo';

function Dot({ color }: { color: Semaforo }) {
  const cls =
    color === 'verde'
      ? 'bg-green-500'
      : color === 'rojo'
        ? 'bg-red-500'
        : 'bg-slate-300 dark:bg-slate-600';
  return <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${cls}`} />;
}

function SectionCard({
  icon: Icon,
  title,
  children,
  className,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
  /** Para que una tarjeta pueda ocupar las dos columnas de la grilla. */
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardBody className="space-y-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <Icon className="h-4 w-4 text-slate-400" /> {title}
        </p>
        {children}
      </CardBody>
    </Card>
  );
}

const BACKUP_ESTADOS: Record<'ok' | 'atrasado' | 'incompleto' | 'ilegible', string> = {
  ok: 'Al día',
  atrasado: 'ATRASADO — no corre hace más de 26 h',
  incompleto: 'El último backup NO terminó completo',
  ilegible: 'Sin acceso al log del backup',
};

const CANAL_ESTADOS: Record<string, { texto: string; color: Semaforo }> = {
  CONNECTED: { texto: 'Conectado', color: 'verde' },
  CONNECTING: { texto: 'Conectando…', color: 'gris' },
  RECONNECTING: { texto: 'Reconectando…', color: 'gris' },
  QR_REQUIRED: { texto: 'Hay que escanear el QR', color: 'gris' },
  DISCONNECTED: { texto: 'Desconectado', color: 'gris' },
  BANNED: { texto: 'Número bloqueado por WhatsApp', color: 'rojo' },
  ERROR: { texto: 'Con error', color: 'rojo' },
};

function CanalRow({ c }: { c: CanalStatus }) {
  const meta = CANAL_ESTADOS[c.estado] ?? { texto: c.estado, color: 'gris' as Semaforo };
  return (
    <div className="rounded-lg border border-surface-border p-3 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <Dot color={meta.color} />
        <p className="min-w-0 flex-1 truncate font-medium text-slate-800 dark:text-slate-100">
          {c.nombre}
        </p>
        <Badge variant={meta.color === 'verde' ? 'success' : meta.color === 'rojo' ? 'danger' : 'default'}>
          {meta.texto}
        </Badge>
        {c.warmup && (
          <Badge variant="warning">
            Calentando: día {c.warmup.dia} de {c.warmup.totalDias} ({c.warmup.enviadosHoy}/
            {c.warmup.topeDiario} hoy)
          </Badge>
        )}
      </div>
      {/* ⚠️ El volumen del canal. Sin esto, un canal que no es de este cliente
          se ve igual que el del cliente: el nombre solo no alcanza. */}
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {c.conversaciones} conversacion{c.conversaciones === 1 ? '' : 'es'} · {c.contactos}{' '}
        contacto{c.contactos === 1 ? '' : 's'}
      </p>
      <div className="mt-1.5 flex flex-wrap gap-x-4 text-xs text-slate-500 dark:text-slate-400">
        <span>
          Último mensaje recibido:{' '}
          {c.ultimoEntranteAt ? relativeTime(c.ultimoEntranteAt) : 'ninguno todavía'}
        </span>
        <span>
          Último enviado:{' '}
          {c.ultimoSalienteAt ? relativeTime(c.ultimoSalienteAt) : 'ninguno todavía'}
        </span>
      </div>
      {c.ultimoError && meta.color !== 'verde' && (
        <p className="mt-1 truncate text-xs text-red-500">{c.ultimoError}</p>
      )}
    </div>
  );
}

function segundos(ms: number | null): string {
  if (ms == null) return '—';
  return `${(ms / 1000).toFixed(1)} s`;
}

function AgenteRow({ a }: { a: AgenteStatus }) {
  const activo = a.estado === 'ACTIVE';
  const color: Semaforo = !activo ? 'gris' : a.enHorario ? 'verde' : 'gris';
  return (
    <div className="rounded-lg border border-surface-border p-3 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <Dot color={color} />
        <p className="min-w-0 flex-1 truncate font-medium text-slate-800 dark:text-slate-100">
          {a.nombre}
        </p>
        <Badge variant={a.motor === 'cli' ? 'info' : 'brand'}>
          {a.motor === 'cli' ? 'Motor CLI (Max)' : 'Motor API'}
        </Badge>
        <Badge variant={!activo ? 'default' : a.enHorario ? 'success' : 'warning'}>
          {!activo ? 'Pausado' : a.enHorario ? 'En horario' : 'Fuera de horario'}
        </Badge>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 text-xs text-slate-500 dark:text-slate-400">
        <span>Turnos atendidos hoy: {a.turnosHoy}</span>
        <span>
          Último turno: {a.ultimoTurnoAt ? relativeTime(a.ultimoTurnoAt) : 'ninguno hoy'}
        </span>
        <span>Respuesta promedio hoy: {segundos(a.promedioRespuestaMs)}</span>
      </div>
    </div>
  );
}

const WORKER_NOMBRES: Record<string, string> = {
  'sla-worker': 'Vigilante de trabajos vencidos (SLA)',
  'reminder-worker': 'Recordatorios de citas',
};

function WorkerRow({ w }: { w: WorkerStatus }) {
  const color: Semaforo = w.vencido ? 'rojo' : w.lastRunAt ? 'verde' : 'gris';
  return (
    <div className="flex items-center gap-2 rounded-lg border border-surface-border p-3 dark:border-slate-700">
      <Dot color={color} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
          {WORKER_NOMBRES[w.nombre] ?? w.nombre}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {w.lastRunAt
            ? `Última corrida ${relativeTime(w.lastRunAt)} · próxima ${shortTime(w.nextDueAt)}`
            : `Sin corridas desde el arranque · primera ${shortTime(w.nextDueAt)}`}
          {w.ultimoError ? ` · último fallo: ${w.ultimoError}` : ''}
        </p>
      </div>
      <Badge variant={w.vencido ? 'danger' : w.lastRunAt ? 'success' : 'default'}>
        {w.vencido ? 'No responde' : w.lastRunAt ? 'Funcionando' : 'Arrancando'}
      </Badge>
    </div>
  );
}

/**
 * Errores del sistema HUMANIZADOS (rediseño): resumen por nivel, filas
 * agrupadas con contador (×175, no 175 filas), filtro por nivel y el RUIDO
 * colapsado por defecto. El mensaje técnico queda en el detalle expandible.
 */
function ErroresDelSistema({
  total,
  grupos,
}: {
  total: number;
  grupos: Array<{ message: string; level: string; count: number; ultimaAt: string | null }>;
}) {
  const [filtro, setFiltro] = useState<NivelError | 'todos'>('todos');

  if (total === 0) {
    return (
      <ConfigRow
        color="verde"
        titulo="Sin errores registrados"
        detalle="Capturados automáticamente del log del servidor."
      />
    );
  }

  const clasificados = grupos
    .map((g) => ({ ...g, humano: humanizeSystemError(g.message) }))
    .sort(
      (a, b) =>
        NIVEL_META[a.humano.nivel].orden - NIVEL_META[b.humano.nivel].orden || b.count - a.count,
    );
  const porNivel = (nivel: NivelError) =>
    clasificados.filter((g) => g.humano.nivel === nivel).reduce((acc, g) => acc + g.count, 0);
  const graves = porNivel('grave');
  const avisos = porNivel('aviso');
  const ruido = porNivel('ruido');

  const visibles = clasificados.filter(
    (g) => (filtro === 'todos' || g.humano.nivel === filtro) && g.humano.nivel !== 'ruido',
  );
  const filasRuido = clasificados.filter(
    (g) => g.humano.nivel === 'ruido' && (filtro === 'todos' || filtro === 'ruido'),
  );

  const niveles: Array<NivelError | 'todos'> = ['todos', 'grave', 'aviso', 'ruido'];

  return (
    <div className="space-y-2.5">
      {/* Resumen semáforo + filtro. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {graves > 0 && <span className="mr-2">🔴 {graves} grave{graves === 1 ? '' : 's'}</span>}
          {avisos > 0 && <span className="mr-2">🟡 {avisos} aviso{avisos === 1 ? '' : 's'}</span>}
          {ruido > 0 && <span>⚪ {ruido} ruido</span>}
        </p>
        <div className="flex gap-1">
          {niveles.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setFiltro(n)}
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${
                filtro === n
                  ? 'bg-slate-700 text-white dark:bg-slate-200 dark:text-slate-900'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400'
              }`}
            >
              {n === 'todos' ? 'Todos' : `${NIVEL_META[n].emoji} ${NIVEL_META[n].label}`}
            </button>
          ))}
        </div>
      </div>

      {/* Graves y avisos: visibles directo. */}
      <ul className="space-y-1.5">
        {visibles.map((g) => (
          <FilaError key={g.message} grupo={g} />
        ))}
      </ul>

      {/* Ruido: colapsado por defecto (reintentos y latencias sin acción). */}
      {filasRuido.length > 0 && (
        <details className="rounded-md border border-surface-border dark:border-slate-700" open={filtro === 'ruido'}>
          <summary className="cursor-pointer px-2.5 py-1.5 text-xs text-slate-500 dark:text-slate-400">
            ⚪ Ruido ({ruido}) — reintentos automáticos y latencias sin acción necesaria
          </summary>
          <ul className="space-y-1.5 px-2.5 pb-2">
            {filasRuido.map((g) => (
              <FilaError key={g.message} grupo={g} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Una fila agrupada: etiqueta humana + ×N + última vez + técnico en detalle. */
function FilaError({
  grupo,
}: {
  grupo: {
    message: string;
    count: number;
    ultimaAt: string | null;
    humano: { etiqueta: string; nivel: NivelError; catalogado: boolean };
  };
}) {
  const tono =
    grupo.humano.nivel === 'grave'
      ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200'
      : grupo.humano.nivel === 'aviso'
        ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
        : 'bg-slate-50 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300';
  return (
    <li className={`rounded-md px-2.5 py-1.5 text-xs ${tono}`}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">
          {NIVEL_META[grupo.humano.nivel].emoji} {grupo.humano.etiqueta}
        </span>
        {grupo.count > 1 && <span className="font-semibold">×{grupo.count} hoy</span>}
        {grupo.ultimaAt && <span className="opacity-70">última {relativeTime(grupo.ultimaAt)}</span>}
      </div>
      <details className="mt-0.5 opacity-80" open={!grupo.humano.catalogado}>
        <summary className="cursor-pointer text-[11px]">detalle técnico</summary>
        <p className="break-all text-[11px]">{grupo.message}</p>
      </details>
    </li>
  );
}

function ConfigRow({
  color,
  titulo,
  detalle,
}: {
  color: Semaforo;
  titulo: string;
  detalle: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-surface-border p-3 dark:border-slate-700">
      <Dot color={color} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{titulo}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{detalle}</p>
      </div>
    </div>
  );
}

export default function Sistema() {
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['system-status'],
    queryFn: getSystemStatus,
    refetchInterval: 10_000,
  });

  const vps = data?.vps;
  const vpsColor: Semaforo = !vps?.configurado ? 'gris' : vps.ping?.ok ? 'verde' : 'rojo';
  const vpsReporte =
    vps?.ping?.reporte && typeof vps.ping.reporte === 'object'
      ? JSON.stringify(vps.ping.reporte)
      : null;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Estado del sistema"
        description="La sala de máquinas: qué está funcionando ahora mismo, sin jerga."
        action={
          <button
            type="button"
            onClick={() => void refetch()}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            title="Actualizar ahora"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            {dataUpdatedAt ? `Actualizado ${relativeTime(new Date(dataUpdatedAt).toISOString())}` : ''}
          </button>
        }
      />

      {isLoading || !data ? (
        <Cargando tamano="pantalla" />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* Saneamiento de datos. Va arriba de todo a proposito: es lo unico
              de esta pantalla que pide una DECISION, el resto es lectura. */}
          <SectionCard icon={Wrench} title="Saneamiento de datos" className="xl:col-span-2">
            <Saneamiento />
          </SectionCard>

          <SectionCard icon={Radio} title="Canales de WhatsApp">
            {data.canales.length === 0 ? (
              <p className="text-sm text-slate-400">No hay canales creados.</p>
            ) : (
              <div className="space-y-2">
                {data.canales.map((c) => (
                  <CanalRow key={c.id} c={c} />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard icon={Bot} title="Agentes de IA">
            <p className="text-xs text-slate-400">
              Horario configurado:{' '}
              {data.horario.horario_activo
                ? `${data.horario.responder_desde}:00 a ${data.horario.responder_hasta}:00`
                : 'responde a toda hora'}
            </p>
            <div className="space-y-2">
              {data.agentes.map((a) => (
                <AgenteRow key={a.id} a={a} />
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={Timer} title="Procesos internos">
            {data.workers.length === 0 ? (
              <p className="text-sm text-slate-400">
                Los procesos aún no reportan (el servidor acaba de arrancar).
              </p>
            ) : (
              <div className="space-y-2">
                {data.workers.map((w) => (
                  <WorkerRow key={w.nombre} w={w} />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard icon={Server} title="Puente VPS (motor CLI/Max)">
            <ConfigRow
              color={vpsColor}
              titulo={
                !vps?.configurado
                  ? 'Sin configurar'
                  : vps.ping?.ok
                    ? `Conectado (respondió en ${vps.ping.latencyMs} ms)`
                    : 'Configurado pero NO responde'
              }
              detalle={
                !vps?.configurado
                  ? 'Falta VPS_AGENT_URL/VPS_AGENT_TOKEN. El análisis de aprendizaje queda en cola hasta configurarlo.'
                  : vps.ping?.ok
                    ? vpsReporte
                      ? `El puente reporta: ${vpsReporte}`
                      : 'El puente no reporta límites de uso.'
                    : (vps.ping?.error ?? 'Sin respuesta del puente.')
              }
            />
          </SectionCard>

          <SectionCard icon={Mail} title="Correo">
            <ConfigRow
              color={data.correo.configurado ? 'verde' : 'gris'}
              titulo={data.correo.configurado ? 'Configurado' : 'Sin configurar'}
              detalle={
                data.correo.ultimoEnvio
                  ? `Último envío ${relativeTime(data.correo.ultimoEnvio.at)} (${data.correo.ultimoEnvio.ok ? 'salió bien' : 'falló'})`
                  : 'Sin envíos desde el arranque.'
              }
            />
          </SectionCard>

          <SectionCard icon={Mic} title="Transcripción de voz">
            <ConfigRow
              color={data.voz.configurado ? 'verde' : 'gris'}
              titulo={
                data.voz.configurado
                  ? `Activa (${data.voz.proveedor === 'groq' ? 'Groq/Whisper' : 'servicio propio'})`
                  : 'Sin configurar'
              }
              detalle={
                data.voz.ultimaTranscripcion
                  ? `Última nota de voz ${relativeTime(data.voz.ultimaTranscripcion.at)} (${data.voz.ultimaTranscripcion.ok ? 'transcribió bien' : 'no se pudo'})`
                  : 'Sin notas de voz desde el arranque.'
              }
            />
          </SectionCard>

          {/* F4b — señales del guardián: backup, disco y errores del sistema. */}
          <SectionCard icon={DatabaseBackup} title="Backup diario">
            <ConfigRow
              color={data.salud.backup.estado === 'ok' ? 'verde' : 'rojo'}
              titulo={BACKUP_ESTADOS[data.salud.backup.estado]}
              detalle={
                data.salud.backup.ultimaCorridaAt
                  ? `Última corrida ${relativeTime(data.salud.backup.ultimaCorridaAt)}`
                  : 'Sin registro de corridas (¿mount del log ausente?).'
              }
            />
          </SectionCard>

          <SectionCard icon={HardDrive} title="Disco del servidor">
            <ConfigRow
              color={
                data.salud.discoPct == null
                  ? 'gris'
                  : data.salud.discoPct >= 85
                    ? 'rojo'
                    : 'verde'
              }
              titulo={data.salud.discoPct == null ? 'Sin lectura' : `${data.salud.discoPct}% usado`}
              detalle={
                data.salud.discoPct != null && data.salud.discoPct >= 85
                  ? 'Arriba del umbral (85%): correr limpieza de imágenes o ampliar disco.'
                  : 'Con espacio suficiente.'
              }
            />
          </SectionCard>

          <SectionCard icon={AlertOctagon} title="Errores del sistema (24 h)">
            <ErroresDelSistema
              total={data.salud.errores24h}
              grupos={data.salud.erroresAgrupados}
            />
          </SectionCard>
        </div>
      )}
      <p className="flex items-center gap-1.5 text-xs text-slate-400">
        <Activity className="h-3.5 w-3.5" /> Esta página se actualiza sola cada 10 segundos.
      </p>
    </div>
  );
}
